import { get } from "lodash"
import { numberFormatter } from "helpers/formatters/numbers"
import { sanitizePositionSet, isIntradaySimulationEligible } from "../helpers"
import { generateQueryKey } from "connected-components/dashboard/helpers/query-key"

function getContentSetExposureSubquery(dateField) {
  return `exposure(contentSetId: $contentSetId) {
        ${dateField}
        factors {
          id
          name
          category
          net
        }
      }`
}

function buildQuery(shouldUseIntradayQuery) {
  if (shouldUseIntradayQuery) {
    // IntradayExposure exposes `timestamp: DateTime!`, not `date` — alias it so downstream select stays uniform
    const exposureSubquery = getContentSetExposureSubquery("date: timestamp")
    return `#multi-date-content-set-exposure
query MultiDateContentSetExposureQuery($modelId: String!, $contentSetId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!) {
  model(id: $modelId) {
    simulation:intradaySimulation(positionSet: $positionSet, base: $base, on: $on) {
      ${exposureSubquery}
    }
  }
}`
  }

  const exposureSubquery = getContentSetExposureSubquery("date")
  return `#multi-date-content-set-exposure
query MultiDateContentSetExposureQuery($modelId: String!, $contentSetId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!) {
  model(id: $modelId) {
    simulation(positionSet: $positionSet, base: $base, from: $from, to: $to, interval: AUTO) {
      ${exposureSubquery}
    }
  }
}`
}

const DAY_BASED_QUERY = buildQuery(false)
const INTRADAY_QUERY = buildQuery(true)

export function createContentSetExposureMultiDateQuery(modelId, contentSetId, positionSet, base, from, to, isIntradayMode, options = {}) {
  const shouldUseIntradaySimulation = isIntradaySimulationEligible(isIntradayMode, from, to, options.batchId, options.lastIntradayBatchId)
  const sanitizedPositionSet = sanitizePositionSet(positionSet)

  const key = generateQueryKey("content-set.exposure.multiDate", {
    contentSetId, modelId, positionSet, base, from, to, intraday: shouldUseIntradaySimulation || undefined,
  })

  const sharedVariables = { contentSetId, modelId, positionSet: sanitizedPositionSet, base }

  const variables = shouldUseIntradaySimulation
    ? { ...sharedVariables, on: from }
    : { ...sharedVariables, from, to }

  return {
    key,
    query: shouldUseIntradaySimulation ? INTRADAY_QUERY : DAY_BASED_QUERY,
    variables,
    select: (graphqlResult) => {
      const exposure = get(graphqlResult, "data.model.simulation.exposure", [])
      return {
        values: {
          formatter: numberFormatter,
          dates: exposure.map(({ date, ...dateData }) => {
            const factors = dateData.factors.reduce((acc, { id, net }) => {
              acc[id] = net
              return acc
            }, {})

            return {
              date,
              ...factors,
            }
          }),
        },
      }
    },
  }
}
